/**
 * Locale identifiers for the Nxtpath tenant console surface.
 *
 * The console ships bilingual (English + Simplified Chinese). Every entry
 * in this union needs a full dictionary: `context.tsx` annotates its
 * `DICTIONARIES` map as `Readonly<Record<Locale, …>>`, so widening the
 * union without adding the catalog is a compile error, not a runtime
 * fallback. That is deliberate — never paper over it with a cast.
 *
 * Order is the order the switcher renders in.
 */
export type Locale = 'en' | 'zh-CN';

/**
 * Runtime list of supported locales — the switcher's option source.
 * Typed `readonly Locale[]` so any `.map` / `.forEach` downstream gets the
 * narrow element type.
 */
export const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'zh-CN'];

/**
 * Fallback locale when no `initialLocale` prop is forced, no stored
 * preference is present, and `navigator.language` is neither Chinese
 * nor English. A stored value is honoured only when it names a
 * supported locale (see `isLocale`).
 */
export const DEFAULT_LOCALE: Locale = 'zh-CN';

/**
 * Native-script label per locale. Each option always renders in its own
 * script so a user who cannot read the active locale can still find their
 * own. These labels are UI chrome, not content — they intentionally do
 * NOT live in the translation dictionaries.
 */
export const LOCALE_NATIVE_LABELS: Readonly<Record<Locale, string>> = {
  en: 'English',
  'zh-CN': '简体中文',
};

/**
 * Shape of template parameters accepted by `t()`. Templates reference
 * params via `{name}` — see `format.ts` for the substitution rules.
 * Values coerce through `String()` so both numbers and strings work;
 * callers that need richer formatting (e.g. currency) should pre-format
 * upstream and pass the display string in.
 */
export type TParams = Readonly<Record<string, string | number>>;

/**
 * Local type guard for `Locale`. Used by the provider to validate a
 * `localStorage` read without an `as`-cast.
 *
 * The membership test runs against `LOCALE_NATIVE_LABELS` rather than a
 * hand-written union of string literals: that object is annotated
 * `Readonly<Record<Locale, string>>`, so widening the `Locale` union
 * without adding a label is a compile error and the guard extends in
 * lockstep for free. A literal `switch` would have silently kept
 * returning `false` for the new locale — legitimate stored values
 * discarded, session silently downgraded to `en`, nothing red.
 *
 * `Object.hasOwn` is ES2022; `tsconfig.base.json` targets ES2022.
 */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && Object.hasOwn(LOCALE_NATIVE_LABELS, value);
}
